import { emptyFilter, FieldLevel, type FilterSpec } from '@numistic/contracts'
import { fieldsForType } from '@numistic/domain'
import { useMeta } from '@numistic/data'
import { useTranslation } from '@numistic/i18n'
import {
  Alert,
  Badge,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Select,
  Table,
  TBody,
  Td,
  Th,
  THead,
  Tr,
} from '@numistic/ui'
import { createRoute, Link } from '@tanstack/react-router'
import { useState } from 'react'
import { z } from 'zod'

import { FilterBuilder } from '../../meta/filter-builder.js'
import { MetaForm } from '../../meta/meta-form.js'
import { adminRoute } from './layout.js'

export const adminTypesRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: '/types',
  validateSearch: z.object({ type: z.string().optional() }),
  component: TypesPage,
})

function TypesPage() {
  const { t } = useTranslation()
  const meta = useMeta().data
  const search = adminTypesRoute.useSearch()
  const [level, setLevel] = useState<FieldLevel>('catalog')
  const [result, setResult] = useState<Record<string, unknown> | null>(null)
  const [filter, setFilter] = useState<FilterSpec>(emptyFilter())
  if (!meta) return null
  const type = meta.itemTypes.find((x) => x.code === search.type) ?? meta.itemTypes[0]!
  const fields = fieldsForType(meta, type.code, level)

  return (
    <div className="grid gap-6">
      <p className="text-sm text-muted-foreground">{t('admin.typesHint')}</p>
      <nav className="flex flex-wrap gap-2" aria-label={t('admin.typesTitle')}>
        {meta.itemTypes.map((x) => (
          <Link
            key={x.code}
            to="/admin/types"
            search={{ type: x.code }}
            onClick={() => setResult(null)}
            aria-current={x.code === type.code ? 'page' : undefined}
            className="rounded-full border px-3 py-1 text-sm text-muted-foreground hover:text-foreground aria-[current=page]:border-primary aria-[current=page]:text-foreground"
          >
            {x.name.ru}
          </Link>
        ))}
      </nav>
      <div className="flex flex-wrap items-center gap-4 text-sm">
        <span>
          {t('admin.titleTemplate')}:{' '}
          <code className="rounded bg-muted px-1.5 py-0.5">{type.titleTemplate}</code>
        </span>
        <label className="ml-auto flex items-center gap-2">
          {t('admin.level')}
          <Select
            className="w-44"
            value={level}
            onChange={(e) => {
              setLevel(e.target.value as FieldLevel)
              setResult(null)
            }}
          >
            {FieldLevel.options.map((l) => (
              <option key={l} value={l}>
                {t(`admin.levels.${l}`)}
              </option>
            ))}
          </Select>
        </label>
      </div>
      <div className="grid gap-6 xl:grid-cols-2 xl:items-start">
        <Card>
          <CardHeader>
            <CardTitle>{t('admin.typeFields')}</CardTitle>
          </CardHeader>
          <CardContent>
            <Table>
              <THead>
                <Tr>
                  <Th>{t('admin.refFields.name')}</Th>
                  <Th>{t('admin.refFields.code')}</Th>
                  <Th>{t('admin.dataType')}</Th>
                  <Th>{t('admin.level')}</Th>
                </Tr>
              </THead>
              <TBody>
                {fields.map((f) => (
                  <Tr key={f.def.code}>
                    <Td>
                      {f.def.name.ru}
                      {f.required ? <span className="ml-0.5 text-destructive">*</span> : null}
                    </Td>
                    <Td className="font-mono text-xs text-muted-foreground">{f.def.code}</Td>
                    <Td>
                      <Badge variant="muted">{f.def.dataType}</Badge>
                    </Td>
                    <Td className="text-xs text-muted-foreground">
                      {f.override ? '↳ ' : ''}
                      {f.def.levels.map((l) => t(`admin.levels.${l}`)).join(', ')}
                    </Td>
                  </Tr>
                ))}
              </TBody>
            </Table>
          </CardContent>
        </Card>
        <div className="grid gap-6">
          <Card>
            <CardHeader>
              <CardTitle>{t('admin.typePreview')}</CardTitle>
              <CardDescription>{t('admin.typePreviewHint')}</CardDescription>
            </CardHeader>
            <CardContent>
              <MetaForm
                key={`${type.code}-${level}`}
                typeCode={type.code}
                level={level}
                onSubmit={setResult}
                submitLabel={t('common.save')}
                footer={
                  result ? (
                    <Alert variant="success" className="grid gap-2">
                      <span>{t('admin.typePreviewValid')}</span>
                      <pre className="overflow-x-auto text-xs" data-testid="preview-result">
                        {JSON.stringify(result, null, 2)}
                      </pre>
                    </Alert>
                  ) : null
                }
              />
            </CardContent>
          </Card>
          <Card>
            <CardHeader>
              <CardTitle>{t('filter.title')}</CardTitle>
            </CardHeader>
            <CardContent className="grid gap-3">
              <FilterBuilder
                meta={meta}
                value={filter}
                onChange={setFilter}
                level={level}
                typeCode={type.code}
              />
              <pre
                className="overflow-x-auto rounded bg-muted p-2 text-xs"
                data-testid="filter-spec"
              >
                {JSON.stringify(filter)}
              </pre>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  )
}
